/**
 * @file `anchorPage` — recompute the current page number when a list's page
 *       size changes, keeping the first visible row on screen instead of
 *       snapping back to page 1.
 * @module @labcraft/admin/ui/hooks/page-anchor
 *
 * Pure (no JSX) so the unit tests can import it — a panel's `.tsx` can't load
 * under Node's type-stripping. Every numbered-pager panel derives its page
 * size from the viewport (`usePageSize` via `usePagedList`), so opening a
 * `LowerSplit` detail pane shrinks the table and shrinks the page size.
 * Resetting to page 1 on every page-size change lost the operator's place the
 * moment they clicked a row; anchoring the top row preserves it across both
 * the detail open/close and a genuine window resize.
 */

/**
 * Map the current page to the page that keeps its first row visible after the
 * page size changes. Returns `currentPage` unchanged when either size is
 * unmeasured (`<= 0`) — including the initial `0 -> N` measure, where the page
 * is already 1. When the size is unchanged the formula is an identity, so the
 * caller needs no separate no-op guard.
 *
 * @param currentPage - The 1-based page the operator is on.
 * @param prevPageSize - The page size before the change (rows per page).
 * @param nextPageSize - The page size after the change (rows per page).
 * @returns The 1-based page whose first row is the old page's first row.
 */
export function anchorPage(
  currentPage: number,
  prevPageSize: number,
  nextPageSize: number,
): number {
  if (prevPageSize <= 0 || nextPageSize <= 0) return currentPage

  const topRow = (currentPage - 1) * prevPageSize
  return Math.floor(topRow / nextPageSize) + 1
}
